import Image from 'next/image';

import { Skeleton, SvgIcon } from '@mui/material';

import { tss } from 'tss-react/mui';

import type {
  ImageLoader,
  StaticImport,
} from 'next/dist/shared/lib/get-img-props';

import { forwardRef } from 'react';

import Group from 'Images/menu/groupIcon.svg';
import { or2ImageLoader } from '@/modules/image/loaders/or2ImageLoader';

const useStyles = tss
  .withParams<{ height: number | undefined }>()
  .create(({ height }) => {
    return {
      image: {
        backgroundSize: 'contain',
        objectFit: 'contain',
      },
      defaultGroupSvg: {
        width: '100%',
        height,
        display: 'block',
      },
    };
  });

interface CommonProps {
  className?: string;
  containerClassName?: string;
  src?: string | StaticImport | null;
  alt: string;
  loading?: boolean;
  quality?: number;
  loader?: ImageLoader;
  unoptimized?: boolean;
}

type ConditionalProps =
  | {
      fill: true;
      size?: never;
      height?: never;
      width?: never;
    }
  | {
      fill?: never;
      size: number;
      height?: never;
      width?: never;
    }
  | {
      fill?: never;
      size?: never;
      height?: number;
      width?: number;
    };

type Or2ImageProps = CommonProps & ConditionalProps;

export const Or2Image = forwardRef<HTMLDivElement, Or2ImageProps>(
  (
    {
      className,
      containerClassName,
      fill,
      src,
      alt,
      loading,
      size,
      height: rawHeight,
      width: rawWidth,
      quality = 100,
      loader = or2ImageLoader,
      unoptimized = false,
    },
    ref,
  ) => {
    const height = rawHeight || size;
    const width = rawWidth || size;

    const { classes, cx } = useStyles({ height });

    return (
      <div className={containerClassName} ref={ref}>
        {src ? (
          <Image
            unoptimized={unoptimized}
            src={typeof src === 'string' ? src.trim() : src}
            alt={alt}
            fill={fill}
            height={height}
            width={width}
            quality={quality}
            className={cx(classes.image, className)}
            loader={loader}
          />
        ) : !loading || src === '' ? (
          <SvgIcon
            viewBox={`0 0 62.5 62.5`}
            className={cx(classes.defaultGroupSvg, className)}
            component={Group}
          />
        ) : (
          <Skeleton
            variant="rectangular"
            height={String(height)}
            width={String(width)}
            animation="wave"
            sx={{ width: '100%', height: '100%' }}
          />
        )}
      </div>
    );
  },
);

Or2Image.displayName = 'Or2Image';
